// ============================================================================
// COMPONENTE: Dialog para realizar atendimento de um paciente
// ============================================================================

import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { useAuth } from "../../AuthContext";
import DialogConfirmacao from "./DialogConfirmacao";
import {
  FaUser,
  FaCalendar,
  FaStickyNote,
  FaTooth,
  FaFileMedical,
  FaList,
  FaClock,
  FaDollarSign,
  FaCalendarCheck,
} from "react-icons/fa";

const DialogRealizarAtendimento = ({
  // Controle do diálogo
  visible,
  onHide,

  // Dados do agendamento
  reservaEditando,
  pacienteEscolhido,

  // Estados que vêm do pai (compartilhados)
  queixas,
  setQueixas,
  observacoes,
  setObservacoes,
  observacoesExistentes,

  // Estados do odontograma (compartilhados)
  mostrarAdulto,
  setMostrarAdulto,
  mostrarInfantil,
  setMostrarInfantil,
  selectedDentesAdulto,
  setSelectedDentesAdulto,
  selectedDentesInfantil,
  setSelectedDentesInfantil,
  handleDenteClick,
  handleCondicaoChange,
  historicoVisualMap,
  condicoesVisualHistorico,
  condicoesDentes,
  setCondicoesDentes,
  dentesProcessados,
  setDentesProcessados,
  descricoesDentarias,
  setDescricoesDentarias,
  servicosSelecionados,
  setServicosSelecionados,
  servicoSelecionadoId,
  setServicoSelecionadoId,
  servicosFiltrados,
  valorTotal,
  setValorTotal,
  calcularTotalComOriginal,

  // Componentes memoizados
  DentesAdultoMemo,
  DentesInfantilMemo,

  // Funções auxiliares
  formatarData,
  formatarHorario,

  // Callbacks do pai
  fetchAgendamentos,
  fetchTodosDados,
  showSuccessMessage,
  showErrorMessage,
  resetarCampos,
  setReservaEditando,
  setDialogFinalizar,
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [descricaoAtual, setDescricaoAtual] = useState("");

  // 🆕 NOVO ESTADO: Lista unificada de serviços (original + adicionais)
  const [servicosAtendimento, setServicosAtendimento] = useState([]);

  // 🆕 Estados para o diálogo de confirmação
  const [showConfirmRemoveOriginal, setShowConfirmRemoveOriginal] =
    useState(false);
  const [servicoParaRemover, setServicoParaRemover] = useState(null);

  // 🆕 Estado para controlar se o usuário personalizou o valor total
  const [valorTotalPersonalizado, setValorTotalPersonalizado] = useState(false);

  const [isUpdating, setIsUpdating] = useState(false);
  const isRemovingRef = useRef(false);

  // ==========================================================================
  // DETECÇÃO DE DISPOSITIVO MÓVEL
  // ==========================================================================
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // ==========================================================================
  // 🆕 EFECT: Inicializar a lista de serviços quando o diálogo abre
  // ==========================================================================
  useEffect(() => {
    if (visible && reservaEditando) {
      const servicoOriginal = {
        id: reservaEditando.servico_id,
        descricao: reservaEditando.servico_nome,
        duracao: reservaEditando.servico_duracao || 0,
        valor: reservaEditando.servico_valor
          ? parseFloat(reservaEditando.servico_valor)
          : 0,
        tipo: "original",
        dataAdicao: new Date().toISOString(),
      };

      const adicionais = servicosSelecionados.map((serv) => ({
        ...serv,
        tipo: "adicional",
      }));

      setServicosAtendimento([servicoOriginal, ...adicionais]);

      // Resetar personalização ao abrir o diálogo
      setValorTotalPersonalizado(false);
    }
  }, [visible, reservaEditando]);

  // ==========================================================================
  // 🆕 FUNÇÃO: Calcular total baseado na lista unificada
  // ==========================================================================
  const calcularTotalServicos = () => {
    if (!servicosAtendimento || servicosAtendimento.length === 0) return 0;

    const total = servicosAtendimento.reduce((soma, servico) => {
      const valor = servico.valor ? parseFloat(servico.valor) : 0;
      return soma + valor;
    }, 0);

    return total;
  };

  // ==========================================================================
  // 🆕 EFECT: Sincronizar valorTotal com o total dos serviços
  // ==========================================================================
  useLayoutEffect(() => {
    // Se estiver removendo, não faça nada
    if (isRemovingRef.current) return;

    // Se não há serviços, sempre reseta para 0
    if (servicosAtendimento.length === 0) {
      // Só atualiza se o valor não for já 0
      if (valorTotal !== "0,00") {
        setValorTotal("0,00");
        setValorTotalPersonalizado(false);
      }
      return;
    }

    // Se o usuário NÃO personalizou o valor, atualiza automaticamente
    if (!valorTotalPersonalizado) {
      const totalCalculado = calcularTotalServicos();
      const novoTotal = formatarValor(totalCalculado).replace(".", ",");
      if (valorTotal !== novoTotal) {
        setValorTotal(novoTotal);
      }
    }
  }, [
    servicosAtendimento,
    valorTotalPersonalizado,
    calcularTotalServicos,
    valorTotal,
  ]);

  // ==========================================================================
  // FUNÇÃO: Formatar valor monetário
  // ==========================================================================
  const formatarValor = (valor) => {
    if (!valor && valor !== 0) return "0,00";
    return parseFloat(valor).toFixed(2);
  };

  // ==========================================================================
  // FUNÇÃO: Adicionar descrição
  // ==========================================================================
  const handleAdicionarDescricao = () => {
    if (!descricaoAtual.trim()) return;

    const dentesSelecionados = [
      ...selectedDentesAdulto,
      ...selectedDentesInfantil,
    ];

    if (dentesSelecionados.length === 0) return;

    const novaDescricao = {
      id: Date.now(),
      dentes: dentesSelecionados
        .map(
          (d) =>
            `${d.numero} (${d.situacao === 1 ? "Pendente" : "Finalizado"})`,
        )
        .join(", "),
      descricao: descricaoAtual.trim(),
      dataAdicao: new Date().toISOString(),
      dentesArray: dentesSelecionados.map((d) => d.numero),
      situacoes: dentesSelecionados.reduce((acc, d) => {
        acc[d.numero] = d.situacao;
        return acc;
      }, {}),
    };

    setDescricoesDentarias([...descricoesDentarias, novaDescricao]);
    setDescricaoAtual("");

    const novosDentesProcessados = [...dentesProcessados];
    dentesSelecionados.forEach((dente) => {
      const existe = novosDentesProcessados.find(
        (d) => d.numero === dente.numero,
      );
      if (!existe) {
        novosDentesProcessados.push({
          numero: dente.numero,
          situacao: dente.situacao,
        });
      }
    });
    setDentesProcessados(novosDentesProcessados);

    setSelectedDentesAdulto([]);
    setSelectedDentesInfantil([]);
  };

  // ==========================================================================
  // FUNÇÃO: Remover descrição
  // ==========================================================================
  const handleRemoverDescricao = (id) => {
    setDescricoesDentarias(
      descricoesDentarias.filter((item) => item.id !== id),
    );
  };

  // ==========================================================================
  // 🆕 FUNÇÃO: Adicionar serviço (agora adiciona à lista unificada)
  // ==========================================================================
  const handleAdicionarServico = () => {
    if (!servicoSelecionadoId) {
      alert("Selecione um serviço primeiro!");
      return;
    }

    setLoading(true);

    const servicoCompleto = servicosFiltrados.find(
      (s) => s.value === servicoSelecionadoId,
    );

    if (!servicoCompleto) {
      alert("Serviço não encontrado!");
      setLoading(false);
      return;
    }

    // Verificar se o serviço já não está na lista (evitar duplicidade)
    const jaExiste = servicosAtendimento.some(
      (serv) => serv.id === servicoCompleto.value,
    );

    if (jaExiste) {
      alert("Este serviço já foi adicionado!");
      setLoading(false);
      return;
    }

    const novoServico = {
      id: servicoCompleto.value,
      descricao: servicoCompleto.nome,
      duracao: servicoCompleto.duracao,
      valor: servicoCompleto.valor,
      dataAdicao: new Date().toISOString(),
      tipo: "adicional",
    };

    setServicosAtendimento([...servicosAtendimento, novoServico]);
    setServicoSelecionadoId(null);
    // Resetar personalização ao adicionar serviço
    setValorTotalPersonalizado(false);
    setLoading(false);
  };

  // ==========================================================================
  // 🆕 FUNÇÃO: Remover serviço (com confirmação para o original)
  // ==========================================================================
  const handleRemoverServico = (servico) => {
    if (servico.tipo === "original") {
      setServicoParaRemover(servico);
      setShowConfirmRemoveOriginal(true);
      return;
    }

    // Prevenir o comportamento fantasma
    const novosServicos = servicosAtendimento.filter(
      (serv) => serv.id !== servico.id,
    );

    // Se a lista ficar vazia, zera imediatamente
    if (novosServicos.length === 0) {
      setValorTotal("0,00");
      setValorTotalPersonalizado(false);
    }

    setServicosAtendimento(novosServicos);
  };

  // ==========================================================================
  // 🆕 FUNÇÃO: Confirmar remoção do serviço original
  // ==========================================================================
  const confirmarRemoverOriginal = () => {
    if (servicoParaRemover) {
      const novosServicos = servicosAtendimento.filter(
        (serv) => serv.id !== servicoParaRemover.id,
      );
      setServicosAtendimento(novosServicos);
      // Resetar personalização ao remover serviço original
      setValorTotalPersonalizado(false);
    }
    setShowConfirmRemoveOriginal(false);
    setServicoParaRemover(null);
  };

  // ==========================================================================
  // 🆕 FUNÇÃO: Cancelar remoção do serviço original
  // ==========================================================================
  const cancelarRemoverOriginal = () => {
    setShowConfirmRemoveOriginal(false);
    setServicoParaRemover(null);
  };

  // ==========================================================================
  // HANDLER: Finalizar atendimento (chamada à API)
  // ==========================================================================
  const handleFinalizarAtendimento = async () => {
    setLoading(true);

    const agendamentoId = reservaEditando?.id;

    try {
      const todosProcedimentosParaSalvar = [];

      // 1. ADICIONAR TODAS AS DESCRIÇÕES EXISTENTES
      descricoesDentarias.forEach((descricao) => {
        todosProcedimentosParaSalvar.push({
          ...descricao,
          tipo: "descricao",
        });
      });

      // 2. ADICIONAR PROCEDIMENTOS PARA CADA DENTE COM CONDIÇÃO
      Object.keys(condicoesDentes).forEach((numeroDente) => {
        const condicao = condicoesDentes[numeroDente];
        const jaTemDescricao = descricoesDentarias.some((desc) =>
          desc.dentesArray.some((dente) => dente.includes(numeroDente)),
        );

        if (!jaTemDescricao && condicao) {
          const denteCompleto = `Dente ${numeroDente}`;
          todosProcedimentosParaSalvar.push({
            descricao: "",
            dentes: denteCompleto,
            dentesArray: [denteCompleto],
            situacoes: { [denteCompleto]: 1 },
            condicoes: { [numeroDente]: condicao },
            tipo: "condicao_apenas",
          });
        }
      });

      const temObservacao = observacoes && observacoes.trim() !== "";
      const temProcedimentos = todosProcedimentosParaSalvar.length > 0;

      // Se tiver observação mas não tem procedimentos
      if (temObservacao && !temProcedimentos) {
        const dadosObservacao = {
          descricao: "",
          dentes: "Nenhum dente",
          dentesArray: [],
          situacoes: {},
          observacoes: observacoes,
          empresa_id: reservaEditando.empresa_id,
          paciente_id: reservaEditando.paciente_id,
          profissional_id: reservaEditando.profissional_id,
          agendamento_id: reservaEditando.id,
          servico_id: reservaEditando.servico_id,
          data_realizacao: new Date().toISOString(),
          condicoes: {},
        };

        const responseObservacao = await fetch(
          "https://helder.cantarellisolucoes.com.br/wp-json/custom/v1/procedimentos",
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${user.token}`,
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify(dadosObservacao),
          },
        );

        const dataObservacao = await responseObservacao.json();

        if (!responseObservacao.ok || !dataObservacao.success) {
          throw new Error(
            dataObservacao.message || "Erro ao salvar observação",
          );
        }
      }

      // SALVAR CADA PROCEDIMENTO
      for (const procedimento of todosProcedimentosParaSalvar) {
        let condicoesParaEnviar = {};

        if (procedimento.tipo === "descricao") {
          procedimento.dentesArray.forEach((dente) => {
            const numero = dente.match(/\d+/)?.[0];
            if (numero && condicoesDentes[numero]) {
              condicoesParaEnviar[numero] = condicoesDentes[numero];
            }
          });
        } else {
          condicoesParaEnviar = procedimento.condicoes;
        }

        const dadosProcedimento = {
          descricao: procedimento.descricao || "",
          dentes: procedimento.dentes,
          dentesArray: procedimento.dentesArray,
          situacoes: procedimento.situacoes,
          observacoes: observacoes,
          empresa_id: reservaEditando.empresa_id,
          paciente_id: reservaEditando.paciente_id,
          profissional_id: reservaEditando.profissional_id,
          agendamento_id: reservaEditando.id,
          servico_id: reservaEditando.servico_id,
          data_realizacao: new Date().toISOString(),
          condicoes: condicoesParaEnviar,
        };

        const responseProcedimento = await fetch(
          "https://helder.cantarellisolucoes.com.br/wp-json/custom/v1/procedimentos",
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${user.token}`,
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify(dadosProcedimento),
          },
        );

        const dataProcedimento = await responseProcedimento.json();

        if (!responseProcedimento.ok || !dataProcedimento.success) {
          throw new Error(
            dataProcedimento.message || "Erro ao salvar procedimento",
          );
        }
      }

      // CONTINUAR COM O ATENDIMENTO
      let valorTotalFinal;
      if (valorTotal && parseFloat(valorTotal.replace(",", ".")) > 0) {
        valorTotalFinal = parseFloat(valorTotal.replace(",", "."));
      } else {
        valorTotalFinal = calcularTotalServicos();
      }

      // 🆕 Usar a lista unificada de serviços
      const todosProcedimentos = servicosAtendimento.map((serv) => ({
        id: serv.id,
        descricao: serv.descricao,
        duracao: serv.duracao || 0,
        valor: serv.valor ? parseFloat(serv.valor) : 0,
        tipo:
          serv.tipo ||
          (serv.id === reservaEditando.servico_id ? "original" : "adicional"),
      }));

      const dadosAtendimento = {
        agendamento_id: reservaEditando.id,
        paciente_id: reservaEditando.paciente_id,
        profissional_id: reservaEditando.profissional_id,
        empresa_id: reservaEditando.empresa_id,
        queixas: queixas,
        procedimentos: todosProcedimentos,
        valor_total: valorTotalFinal,
      };

      const responseAtendimento = await fetch(
        "https://helder.cantarellisolucoes.com.br/wp-json/custom/v1/atendimentos/finalizar",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(dadosAtendimento),
        },
      );

      const dataAtendimento = await responseAtendimento.json();

      if (dataAtendimento.success) {
        await fetchAgendamentos();
        await fetchTodosDados();

        // // 🔥 BUSCAR DADOS COMPLETOS DO CAIXA CRIADO
        // let caixaCompleto = null;
        // if (dataAtendimento.caixa_id) {
        //   try {
        //     const responseCaixa = await fetch(
        //       `https://helder.cantarellisolucoes.com.br/wp-json/custom/v1/caixa/${dataAtendimento.caixa_id}`,
        //       {
        //         headers: {
        //           Authorization: `Bearer ${user.token}`,
        //           Accept: "application/json",
        //         },
        //       },
        //     );
        //     const caixaData = await responseCaixa.json();
        //     if (caixaData.success && caixaData.data) {
        //       caixaCompleto = caixaData.data;
        //       window.caixaCompleto = caixaCompleto;
        //     }
        //   } catch (error) {
        //     console.error("❌ Erro ao buscar dados do caixa:", error);
        //   }
        // }

        // Limpar estados
        setServicosAtendimento([]);
        setServicosSelecionados([]);
        setSelectedDentesAdulto([]);
        setSelectedDentesInfantil([]);
        setServicoSelecionadoId(null);
        setReservaEditando(null);
        setCondicoesDentes({});
        setDescricoesDentarias([]);
        setDescricaoAtual("");
        setObservacoes("");
        setQueixas("");
        setDentesProcessados([]);

        resetarCampos();

        onHide();
        setDialogFinalizar(false);

        showSuccessMessage(
          "Atendimento finalizado com sucesso!",
          true,
          agendamentoId,
        );
      } else {
        throw new Error(
          dataAtendimento.message || "Erro ao finalizar atendimento",
        );
      }
    } catch (error) {
      console.error("Erro:", error);
      showErrorMessage(error.message || "Erro ao processar atendimento");
    } finally {
      setLoading(false);
    }
  };

  // ==========================================================================
  // FUNÇÃO: Cancelar
  // ==========================================================================
  const handleCancelar = () => {
    setQueixas("");
    setObservacoes("");
    setServicosAtendimento([]);
    setServicosSelecionados([]);
    setSelectedDentesAdulto([]);
    setSelectedDentesInfantil([]);
    setServicoSelecionadoId(null);
    setDescricoesDentarias([]);
    setDescricaoAtual("");
    onHide();
    setDialogFinalizar(false);
  };

  // ==========================================================================
  // ESTILOS COMPARTILHADOS
  // ==========================================================================
  const inputStyle = {
    width: "100%",
    height: "40px",
    fontFamily: "'Rajdhani', sans-serif",
    fontSize: "0.9rem",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    padding: "0 12px",
    transition: "all 0.2s",
    outline: "none",
  };

  const iconStyle = {
    color: "#B83732",
    fontSize: "1rem",
    minWidth: "20px",
  };

  const labelStyle = {
    fontWeight: "600",
    color: "#4a5568",
    fontSize: "0.85rem",
    letterSpacing: "0.3px",
    marginBottom: "6px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  };

  const dropdownItemTemplate = (option) => (
    <span
      style={{
        fontFamily: "'Rajdhani', sans-serif",
        fontSize: "0.9rem",
        padding: "8px 12px",
      }}
    >
      {option.label}
    </span>
  );

  const dropdownValueTemplate = (option, props) => {
    if (!option)
      return (
        <span
          style={{
            fontFamily: "'Rajdhani', sans-serif",
            fontSize: "0.9rem",
            color: "#7f8c8d",
          }}
        >
          {props.placeholder}
        </span>
      );
    return (
      <span
        style={{
          fontFamily: "'Rajdhani', sans-serif",
          fontSize: "0.9rem",
        }}
      >
        {option.label}
      </span>
    );
  };

  // ==========================================================================
  // RENDER PRINCIPAL
  // ==========================================================================
  return (
    <>
      <Dialog
        visible={visible}
        style={{
          width: "90vw",
          maxWidth: "1280px",
          fontFamily: "'Rajdhani', sans-serif",
        }}
        onHide={handleCancelar}
        draggable={false}
        header={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "-25px",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                width: "100%",
              }}
            >
              <div style={{ marginRight: "12px", paddingLeft: "8px" }}>
                <FaCalendarCheck
                  style={{ fontSize: "2.4rem", color: "#B83732" }}
                />
              </div>
              <div
                style={{ display: "flex", flexDirection: "column", flex: 1 }}
              >
                <div
                  style={{
                    fontSize: "0.85rem",
                    width: "100%",
                    letterSpacing: "1px",
                    color: "#718096",
                  }}
                >
                  REALIZAR
                </div>
                <div
                  style={{
                    fontSize: "1.8rem",
                    fontWeight: "bold",
                    marginTop: "-8px",
                    color: "#2d3748",
                  }}
                >
                  ATENDIMENTO
                </div>
              </div>
            </div>
          </div>
        }
      >
        {reservaEditando && pacienteEscolhido && (
          <>
            {/* CARD ÚNICO */}
            <div
              style={{
                backgroundColor: "#FAFAFA",
                border: "1px solid #e2e8f0",
                borderRadius: "12px",
                padding: "20px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
              }}
            >
              {/* SEÇÃO 1: INFORMAÇÕES DO PACIENTE E AGENDAMENTO */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                  gap: "16px",
                  marginBottom: "20px",
                  paddingBottom: "10px",
                }}
              >
                <div>
                  <div style={labelStyle}>
                    <FaUser style={iconStyle} />
                    <span>INFORMAÇÕES DO PACIENTE</span>
                  </div>
                  <div style={{ fontSize: "0.9rem", color: "#34495e" }}>
                    <div>
                      <strong>Nome:</strong> {reservaEditando.paciente_nome}
                    </div>
                    <div>
                      <strong>Telefone:</strong>{" "}
                      {reservaEditando.paciente_telefone}
                    </div>
                    <div>
                      <strong>Serviço Original:</strong>{" "}
                      {reservaEditando.servico_nome}
                    </div>
                  </div>
                </div>
                <div>
                  <div style={labelStyle}>
                    <FaCalendar style={iconStyle} />
                    <span>DETALHES DO AGENDAMENTO</span>
                  </div>
                  <div style={{ fontSize: "0.9rem", color: "#34495e" }}>
                    <div>
                      <strong>Profissional:</strong>{" "}
                      {reservaEditando.profissional_nome}
                    </div>
                    <div>
                      <strong>Data:</strong>{" "}
                      {formatarData(reservaEditando.data_atendimento)}
                    </div>
                    <div>
                      <strong>Hora:</strong>{" "}
                      {formatarHorario(reservaEditando.horario)}
                    </div>
                  </div>
                </div>
              </div>

              {/* SEÇÃO 2: QUEIXAS */}
              <div style={{ marginBottom: "20px" }}>
                <div style={labelStyle}>
                  <FaStickyNote style={iconStyle} />
                  <span>QUEIXAS DO ATENDIMENTO </span>
                </div>
                <input
                  type="text"
                  value={queixas}
                  onChange={(e) => setQueixas(e.target.value)}
                  placeholder="Adicione a queixa(s) ao atendimento..."
                  style={inputStyle}
                />
              </div>

              {/* SEÇÃO 3: ODONTOGRAMA */}
              <div style={{ marginBottom: "20px" }}>
                <div style={labelStyle}>
                  <FaTooth style={iconStyle} />
                  <span>ODONTOGRAMA</span>
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "24px",
                    marginBottom: "16px",
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      fontSize: "0.85rem",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={mostrarAdulto}
                      onChange={(e) => setMostrarAdulto(e.target.checked)}
                    />
                    Dentição Permanente (Adulto)
                  </label>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      fontSize: "0.85rem",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={mostrarInfantil}
                      onChange={(e) => setMostrarInfantil(e.target.checked)}
                    />
                    Dentição Decídua (Criança)
                  </label>
                </div>

                <div
                  className="odontograma-scroll"
                  style={{
                    overflowX: "auto",
                    WebkitOverflowScrolling: "touch",
                    marginBottom: "16px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "15px",
                      minWidth: "max-content",
                    }}
                  >
                    {mostrarAdulto && (
                      <DentesAdultoMemo
                        selectedDentesAdulto={selectedDentesAdulto}
                        selectedDentesInfantil={selectedDentesInfantil}
                        onDenteClick={handleDenteClick}
                        onCondicaoChange={handleCondicaoChange}
                        historicoVisual={historicoVisualMap}
                        condicoesVisualHistorico={condicoesVisualHistorico}
                        condicoesDentes={condicoesDentes}
                        dentesProcessados={dentesProcessados}
                      />
                    )}
                    {mostrarInfantil && (
                      <DentesInfantilMemo
                        selectedDentesAdulto={selectedDentesAdulto}
                        selectedDentesInfantil={selectedDentesInfantil}
                        onDenteClick={handleDenteClick}
                        onCondicaoChange={handleCondicaoChange}
                        historicoVisual={historicoVisualMap}
                        condicoesVisualHistorico={condicoesVisualHistorico}
                        condicoesDentes={condicoesDentes}
                        dentesProcessados={dentesProcessados}
                      />
                    )}
                  </div>
                </div>

                {/* Historico */}
                {pacienteEscolhido &&
                  pacienteEscolhido.filter(
                    (item) => item.dentes && item.procedimento_descricao,
                  ).length > 0 && (
                    <div
                      style={{
                        width: "100%",
                        backgroundColor: "#fff",
                        borderRadius: "6px",
                        border: "1px solid #e0e0e0",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: isMobile ? "column" : "row",
                        }}
                      >
                        {/* Pendentes */}
                        <div
                          style={{
                            flex: 1,
                            borderRight: isMobile
                              ? "none"
                              : "1px solid #e0e0e0",
                            borderBottom: isMobile
                              ? "1px solid #e0e0e0"
                              : "none",
                          }}
                        >
                          <div
                            style={{
                              padding: "8px 12px",
                              backgroundColor: "#ffebee",
                              borderBottom: "1px solid #e0e0e0",
                              fontWeight: "600",
                              fontSize: "0.85rem",
                              color: "#c62828",
                            }}
                          >
                            Pendentes (
                            {
                              pacienteEscolhido.filter(
                                (item) =>
                                  item.dentes &&
                                  item.situacao === "1" &&
                                  item.procedimento_descricao,
                              ).length
                            }
                            )
                          </div>
                          <div
                            style={{
                              maxHeight: "180px",
                              overflowY: "auto",
                            }}
                          >
                            {/* Conteúdo dos pendentes */}
                            {pacienteEscolhido
                              .filter(
                                (item) =>
                                  item.dentes &&
                                  item.situacao === "1" &&
                                  item.procedimento_descricao,
                              )
                              .map((item, index) => (
                                <div
                                  key={index}
                                  style={{
                                    padding: "6px 10px",
                                    borderBottom:
                                      index <
                                      pacienteEscolhido.filter(
                                        (item) =>
                                          item.dentes &&
                                          item.situacao === "1" &&
                                          item.procedimento_descricao,
                                      ).length -
                                        1
                                        ? "1px solid #e9ecef"
                                        : "none",
                                    fontSize: "0.75rem",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px",
                                    }}
                                  >
                                    <span
                                      style={{
                                        minWidth: "70px",
                                        color: "#666",
                                      }}
                                    >
                                      {item.data_realizacao
                                        ? new Date(
                                            item.data_realizacao,
                                          ).toLocaleDateString("pt-BR", {
                                            day: "2-digit",
                                            month: "2-digit",
                                          })
                                        : new Date(
                                            item.data_atendimento,
                                          ).toLocaleDateString("pt-BR", {
                                            day: "2-digit",
                                            month: "2-digit",
                                          })}
                                      {item.horario &&
                                        ` - ${item.horario.substring(0, 5)}`}
                                    </span>
                                    <span
                                      style={{
                                        fontWeight: "500",
                                        color: "#c62828",
                                      }}
                                    >
                                      {item.dentes}
                                    </span>
                                    <span style={{ color: "#666", flex: 1 }}>
                                      {item.procedimento_descricao}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            {pacienteEscolhido.filter(
                              (item) =>
                                item.dentes &&
                                item.situacao === "1" &&
                                item.procedimento_descricao,
                            ).length === 0 && (
                              <div
                                style={{
                                  padding: "20px",
                                  textAlign: "center",
                                  color: "#999",
                                  fontSize: "0.75rem",
                                }}
                              >
                                Nenhum pendente com descrição
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Finalizados */}
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              padding: "8px 12px",
                              backgroundColor: "#e3f2fd",
                              borderBottom: "1px solid #e0e0e0",
                              fontWeight: "600",
                              fontSize: "0.85rem",
                              color: "#1976d2",
                            }}
                          >
                            Finalizados (
                            {
                              pacienteEscolhido.filter(
                                (item) =>
                                  item.dentes &&
                                  item.situacao === "2" &&
                                  item.procedimento_descricao,
                              ).length
                            }
                            )
                          </div>
                          <div
                            style={{
                              maxHeight: "180px",
                              overflowY: "auto",
                            }}
                          >
                            {/* Conteúdo dos finalizados */}
                            {pacienteEscolhido
                              .filter(
                                (item) =>
                                  item.dentes &&
                                  item.situacao === "2" &&
                                  item.procedimento_descricao,
                              )
                              .map((item, index) => (
                                <div
                                  key={index}
                                  style={{
                                    padding: "6px 10px",
                                    borderBottom:
                                      index <
                                      pacienteEscolhido.filter(
                                        (item) =>
                                          item.dentes &&
                                          item.situacao === "2" &&
                                          item.procedimento_descricao,
                                      ).length -
                                        1
                                        ? "1px solid #e9ecef"
                                        : "none",
                                    fontSize: "0.75rem",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px",
                                    }}
                                  >
                                    <span
                                      style={{
                                        minWidth: "70px",
                                        color: "#666",
                                      }}
                                    >
                                      {item.data_realizacao
                                        ? new Date(
                                            item.data_realizacao,
                                          ).toLocaleDateString("pt-BR", {
                                            day: "2-digit",
                                            month: "2-digit",
                                          })
                                        : new Date(
                                            item.data_atendimento,
                                          ).toLocaleDateString("pt-BR", {
                                            day: "2-digit",
                                            month: "2-digit",
                                          })}
                                      {item.horario &&
                                        ` - ${item.horario.substring(0, 5)}`}
                                    </span>
                                    <span
                                      style={{
                                        fontWeight: "500",
                                        color: "#1976d2",
                                      }}
                                    >
                                      {item.dentes}
                                    </span>
                                    <span style={{ color: "#666", flex: 1 }}>
                                      {item.procedimento_descricao}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            {pacienteEscolhido.filter(
                              (item) =>
                                item.dentes &&
                                item.situacao === "2" &&
                                item.procedimento_descricao,
                            ).length === 0 && (
                              <div
                                style={{
                                  padding: "20px",
                                  textAlign: "center",
                                  color: "#999",
                                  fontSize: "0.75rem",
                                }}
                              >
                                Nenhum finalizado com descrição
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
              </div>

              {/* SEÇÃO 4: DESCRIÇÕES */}
              <div style={{ marginBottom: "20px" }}>
                <div
                  style={{
                    ...labelStyle,
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: "8px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <FaFileMedical style={iconStyle} />
                    <span>DESCRIÇÕES POR ELEMENTO DENTÁRIO</span>
                  </div>
                  {(selectedDentesAdulto.length > 0 ||
                    selectedDentesInfantil.length > 0) && (
                    <span
                      style={{
                        backgroundColor: "#f39c12",
                        color: "white",
                        padding: "2px 8px",
                        borderRadius: "10px",
                        fontSize: "0.7rem",
                      }}
                    >
                      {selectedDentesAdulto.length +
                        selectedDentesInfantil.length}{" "}
                      dente(s)
                    </span>
                  )}
                </div>

                {/* Container flexível que muda a direção no mobile */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    gap: "8px",
                    marginBottom: "12px",
                  }}
                >
                  <InputText
                    placeholder="Digite a descrição do procedimento..."
                    value={descricaoAtual}
                    onChange={(e) => setDescricaoAtual(e.target.value)}
                    style={{ flex: 1, ...inputStyle }}
                    disabled={
                      selectedDentesAdulto.length === 0 &&
                      selectedDentesInfantil.length === 0
                    }
                  />
                  <Button
                    icon="pi pi-plus"
                    onClick={handleAdicionarDescricao}
                    className="p-button-warning p-button-sm"
                    style={{
                      color: "white",
                      borderRadius: "8px",
                      width: isMobile ? "100%" : "40px",
                      height: isMobile ? "35px%" : "40px",
                    }}
                    disabled={
                      (selectedDentesAdulto.length === 0 &&
                        selectedDentesInfantil.length === 0) ||
                      !descricaoAtual.trim()
                    }
                  />
                </div>

                {descricoesDentarias.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                      maxHeight: "200px",
                      overflowY: "auto",
                    }}
                  >
                    {descricoesDentarias.map((item, index) => (
                      <div
                        key={item.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          padding: "8px",
                          backgroundColor: "white",
                          borderRadius: "8px",
                          border: "1px solid #e2e8f0",
                        }}
                      >
                        <div
                          style={{
                            width: "24px",
                            height: "24px",
                            backgroundColor: "#f39c12",
                            color: "white",
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "0.8rem",
                          }}
                        >
                          {index + 1}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: "500" }}>
                            {item.descricao}
                          </div>
                          <div
                            style={{ fontSize: "0.75rem", color: "#7f8c8d" }}
                          >
                            {item.dentesArray.join(", ")}
                          </div>
                        </div>
                        <Button
                          icon="pi pi-trash"
                          onClick={() => handleRemoverDescricao(item.id)}
                          className="p-button-danger p-button-sm"
                          style={{ width: "32px", height: "32px" }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* SEÇÃO 5: OBSERVAÇÕES */}
              <div style={{ marginBottom: "20px" }}>
                <div style={labelStyle}>
                  <FaStickyNote style={iconStyle} />
                  <span>OBSERVAÇÕES DO ATENDIMENTO</span>
                </div>
                <input
                  type="text"
                  value={observacoes}
                  onChange={(e) => setObservacoes(e.target.value)}
                  placeholder="Adicione observações sobre o atendimento..."
                  style={inputStyle}
                />
                {observacoesExistentes?.length > 0 && (
                  <div style={{ marginTop: "10px" }}>
                    {observacoesExistentes.map((obs, index) => (
                      <div
                        key={index}
                        style={{
                          backgroundColor: "#fff8e1",
                          padding: "8px 12px",
                          borderRadius: "6px",
                          fontSize: "0.85rem",
                          border: "1px solid #ffecb3",
                          marginBottom: "4px",
                        }}
                      >
                        {obs.texto}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* SEÇÃO 6: SERVIÇOS (UNIFICADA - ORIGINAL + ADICIONAIS) */}
              <div style={{ marginBottom: "20px" }}>
                <div
                  style={{
                    ...labelStyle,
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: "8px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <FaList style={iconStyle} />
                    <span>SERVIÇOS</span>
                  </div>
                  <span
                    style={{
                      backgroundColor: "#e8f5e9",
                      color: "#27ae60",
                      padding: "3px 12px",
                      borderRadius: "12px",
                      fontSize: "0.8rem",
                    }}
                  >
                    {servicosAtendimento.length} serviço(s)
                  </span>
                </div>

                {/* Container flexível que muda a direção no mobile */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    gap: "8px",
                    marginBottom: "12px",
                  }}
                >
                  <Dropdown
                    value={servicoSelecionadoId}
                    onChange={(e) => setServicoSelecionadoId(e.value)}
                    options={servicosFiltrados}
                    placeholder="Adicionar novo serviço..."
                    style={{ flex: 1, height: "40px" }}
                    itemTemplate={dropdownItemTemplate}
                    valueTemplate={dropdownValueTemplate}
                  />
                  <Button
                    icon="pi pi-plus"
                    onClick={handleAdicionarServico}
                    className="p-button-success p-button-sm"
                    style={{
                      color: "white",
                      borderRadius: "8px",
                      width: isMobile ? "100%" : "40px",
                      height: isMobile ? "35px%" : "40px",
                    }}
                    disabled={!servicoSelecionadoId}
                  />
                </div>

                {/* Lista unificada de serviços */}
                {servicosAtendimento.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    {servicosAtendimento.map((serv, index) => (
                      <div
                        key={`${serv.id}_${serv.tipo}`}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          padding: "8px",
                          backgroundColor:
                            serv.tipo === "original" ? "#E7F3FF" : "white",
                          borderRadius: "8px",
                          border:
                            serv.tipo === "original"
                              ? "1px solid #BBDEFB"
                              : "1px solid #e2e8f0",
                        }}
                      >
                        <div
                          style={{
                            width: "24px",
                            height: "24px",
                            backgroundColor: "#28a745",
                            color: "white",
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "0.8rem",
                          }}
                        >
                          {index + 1}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: "500" }}>
                            {serv.descricao}
                            {serv.tipo === "original" && (
                              <span
                                style={{
                                  marginLeft: "8px",
                                  fontSize: "0.7rem",
                                  backgroundColor: "#28a745",
                                  color: "white",
                                  padding: "2px 6px",
                                  borderRadius: "4px",
                                }}
                              >
                                ORIGINAL
                              </span>
                            )}
                          </div>
                          <div
                            style={{ fontSize: "0.75rem", color: "#7f8c8d" }}
                          >
                            Duração: {serv.duracao}min • Valor: R${" "}
                            {formatarValor(serv.valor)}
                          </div>
                        </div>
                        <Button
                          icon="pi pi-trash"
                          onClick={() => handleRemoverServico(serv)}
                          className="p-button-danger p-button-sm"
                          style={{ width: "32px", height: "32px" }}
                          tooltip={
                            serv.tipo === "original"
                              ? "Remover serviço original"
                              : "Remover serviço"
                          }
                          tooltipOptions={{ position: "top" }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* SEÇÃO 7: RESUMO FINANCEIRO */}
              <div
                style={{
                  backgroundColor: "#e7f3ff",
                  border: "1px solid #bbdefb",
                  borderRadius: "8px",
                  padding: "15px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "16px",
                  }}
                >
                  <div style={{ textAlign: "center" }}>
                    <div
                      style={{
                        fontSize: "0.85rem",
                        fontWeight: "600",
                        marginBottom: "4px",
                      }}
                    >
                      Valor Total do Atendimento
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        justifyContent: "center",
                      }}
                    >
                      <span style={{ fontSize: "1.5rem", fontWeight: "bold" }}>
                        R$
                      </span>
                      <InputText
                        value={valorTotal}
                        onChange={(e) => {
                          const novoValor = e.target.value;
                          setValorTotal(novoValor);
                          setValorTotalPersonalizado(true); // Marca que o usuário personalizou
                        }}
                        placeholder="0,00"
                        style={{
                          width: "140px",
                          height: "45px",
                          fontSize: "1.8rem",
                          textAlign: "center",
                          fontWeight: "bold",
                          color: "#1565c0",
                          fontFamily: "'Rajdhani', sans-serif",
                        }}
                      />
                    </div>

                    {valorTotalPersonalizado && (
                      <div
                        style={{
                          fontSize: "0.7rem",
                          color: "#e74c3c",
                          marginTop: "8px",
                          backgroundColor: "#fdeaea",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        ⚠️ Valor personalizado (não será atualizado
                        automaticamente)
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* ========== BOTÕES DE AÇÃO ========== */}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "16px",
                flexWrap: "wrap",
                marginTop: "24px",
              }}
            >
              {/* Botão Cancelar */}
              <Button
                label="CANCELAR"
                icon="pi pi-times"
                onClick={handleCancelar}
                className="p-button-outlined"
                style={{
                  padding: "10px 28px",
                  minWidth: "120px",
                  borderColor: "#e2e8f0",
                  color: "#718096",
                  fontWeight: "600",
                  borderRadius: "8px",
                }}
                disabled={loading}
              />

              {/* Botão Finalizar */}
              <Button
                label={loading ? "FINALIZANDO..." : "FINALIZAR ATENDIMENTO"}
                icon="pi pi-check"
                onClick={handleFinalizarAtendimento}
                className="p-button-success p-button-sm"
                style={{
                  padding: "10px 28px",
                  minWidth: "180px",
                  fontWeight: "600",
                  borderRadius: "8px",
                  color: "white",
                }}
                disabled={loading}
              />
            </div>

            {/* ========== CSS PERSONALIZADO ========== */}
            <style>
              {`
              /* Ajuste do ícone do calendário */
              .p-calendar .p-button.p-button-icon-only {
                width: 40px !important;
                height: 40px !important;
                background-color: #f7fafc !important;
                border: 1px solid #e2e8f0 !important;
                border-left: none !important;
                border-top-right-radius: 8px !important;
                border-bottom-right-radius: 8px !important;
                color: #B83732 !important;
              }
              .p-calendar .p-button.p-button-icon-only:hover {
                background-color: #edf2f7 !important;
              }
              .p-calendar .p-button-icon {
                font-size: 0.9rem !important;
              }
              
              /* 🔥 Melhorias para o scroll do odontograma no mobile */
              .odontograma-scroll {
                overflow-x: auto;
                overflow-y: hidden;
                -webkit-overflow-scrolling: touch;
                scroll-behavior: smooth;
                cursor: grab;
              }
              
              .odontograma-scroll:active {
                cursor: grabbing;
              }
              
              /* Esconde a barra de scroll no mobile mas mantém a funcionalidade */
              .odontograma-scroll::-webkit-scrollbar {
                height: 4px;
              }
              
              .odontograma-scroll::-webkit-scrollbar-track {
                background: #f1f1f1;
                border-radius: 4px;
              }
              
              .odontograma-scroll::-webkit-scrollbar-thumb {
                background: #c1c1c1;
                border-radius: 4px;
              }
              
              .odontograma-scroll::-webkit-scrollbar-thumb:hover {
                background: #a8a8a8;
              }
            `}
            </style>
          </>
        )}
      </Dialog>

      {/* Diálogo de confirmação para remover serviço original */}
      <DialogConfirmacao
        visible={showConfirmRemoveOriginal}
        message={`Deseja realmente remover o serviço "${servicoParaRemover?.descricao}" deste atendimento?\n\nO paciente ainda poderá realizar outros serviços, mas este serviço será removido da lista.`}
        titulo="Remover Serviço Original"
        icon="pi-exclamation-triangle"
        iconColor="#B83732"
        onConfirm={confirmarRemoverOriginal}
        onCancel={cancelarRemoverOriginal}
        textoConfirmar="SIM, REMOVER"
        textoCancelar="CANCELAR"
      />
    </>
  );
};

export default DialogRealizarAtendimento;
